// 配信履歴(設計書 §3.4.5)
// 状態 / プロファイルで絞り込み + ページネーション。

import { useState } from 'react';
import { ApiClientError } from '../api/client';
import {
  SESSION_STATE_LABELS,
  useSessionHistory,
  type HistoryQuery,
  type SessionDetail,
  type SessionState,
} from '../api/sessions';
import {
  PLATFORM_LABELS,
  useStreamingProfiles,
  type StreamingProfile,
} from '../api/streaming-profiles';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Select';

const PER_PAGE = 20;

const STATE_OPTIONS: Array<{ value: SessionState | ''; label: string }> = [
  { value: '', label: 'すべての状態' },
  { value: 'running', label: '配信中' },
  { value: 'completed', label: '完了' },
  { value: 'failed', label: '失敗' },
  { value: 'starting', label: '開始中' },
  { value: 'stopping', label: '停止処理中' },
];

export function SessionHistoryPage() {
  const profilesQuery = useStreamingProfiles();
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState<SessionState | ''>('');
  const [profileFilter, setProfileFilter] = useState<string>('');

  const query: HistoryQuery = {
    page,
    per_page: PER_PAGE,
    ...(stateFilter ? { state: stateFilter } : {}),
    ...(profileFilter ? { streaming_profile_ulid: profileFilter } : {}),
  };

  const historyQuery = useSessionHistory(query);
  const profileMap = new Map(profilesQuery.data?.map((p) => [p.ulid, p]) ?? []);

  function applyFilter(setter: () => void) {
    setPage(1);
    setter();
  }

  return (
    <AppLayout
      title="配信履歴"
      description="過去のセッションを確認します。"
    >
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select
            label="状態"
            value={stateFilter}
            onChange={(e) =>
              applyFilter(() => setStateFilter(e.target.value as SessionState | ''))
            }
          >
            {STATE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select
            label="配信先プロファイル"
            value={profileFilter}
            onChange={(e) => applyFilter(() => setProfileFilter(e.target.value))}
          >
            <option value="">すべてのプロファイル</option>
            {(profilesQuery.data ?? []).map((p) => (
              <option key={p.ulid} value={p.ulid}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {historyQuery.isLoading && !historyQuery.data && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {historyQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {historyQuery.error instanceof ApiClientError
              ? historyQuery.error.message
              : '履歴の取得に失敗しました'}
          </p>
        </Card>
      )}

      {historyQuery.data && historyQuery.data.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            該当する履歴はありません。
          </p>
        </Card>
      )}

      {historyQuery.data && historyQuery.data.data.length > 0 && (
        <Card>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary)' }}>
                <Th>開始日時</Th>
                <Th>状態</Th>
                <Th>配信先</Th>
                <Th>配信時間</Th>
                <Th>終了理由</Th>
              </tr>
            </thead>
            <tbody>
              {historyQuery.data.data.map((s) => (
                <SessionRow
                  key={s.ulid}
                  session={s}
                  profile={profileMap.get(s.streaming_profile_ulid)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {historyQuery.data && historyQuery.data.pagination.total_pages > 1 && (
        <Card>
          <Pagination
            page={historyQuery.data.pagination.page}
            totalPages={historyQuery.data.pagination.total_pages}
            total={historyQuery.data.pagination.total}
            onChange={setPage}
            disabled={historyQuery.isFetching}
          />
        </Card>
      )}
    </AppLayout>
  );
}

function SessionRow({
  session,
  profile,
}: {
  session: SessionDetail;
  profile: StreamingProfile | undefined;
}) {
  return (
    <tr style={{ borderTop: 'var(--border-default)' }}>
      <Td>{formatDateTime(session.started_at)}</Td>
      <Td>
        <span
          style={{
            color:
              session.state === 'completed'
                ? 'var(--color-text-primary)'
                : session.state === 'failed'
                  ? 'var(--color-error-text)'
                  : session.state === 'running'
                    ? 'var(--color-success-text)'
                    : 'var(--color-warning-text)',
          }}
        >
          {SESSION_STATE_LABELS[session.state]}
        </span>
      </Td>
      <Td>
        {profile ? (
          <>
            <div style={{ color: 'var(--color-text-primary)' }}>{profile.name}</div>
            <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
              {PLATFORM_LABELS[profile.platform]}
            </div>
          </>
        ) : (
          <span style={{ color: 'var(--color-text-tertiary)' }}>(削除済み)</span>
        )}
      </Td>
      <Td>{formatDuration(session.duration_seconds)}</Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {session.termination_reason ?? '—'}
      </Td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        fontWeight: 500,
        padding: '8px 8px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: '10px 8px',
        verticalAlign: 'top',
        color: 'var(--color-text-primary)',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
        {page} / {totalPages} ページ(全 {total} 件)
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page <= 1}
            onClick={() => onChange(page - 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            前へ
          </Button>
        </div>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page >= totalPages}
            onClick={() => onChange(page + 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            次へ
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h} 時間 ${m} 分`;
  if (m > 0) return `${m} 分 ${s} 秒`;
  return `${s} 秒`;
}
